import { useState } from 'react';
import { Box, Loader2, Copy, RefreshCw, Sparkles } from 'lucide-react';
import { InstrumentLayout } from '@/components/growth-instruments';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useIdeas } from '@/hooks/useIdeas';
import { useIdeaArtifacts } from '@/hooks/useIdeaArtifacts';
import {
  ContentRenderer, formatContentAsText, SourcesAccordion, ConfidenceBadge,
  type SourceRef,
} from '@/components/growth-instruments/ArtifactContent';

function isRecent(updatedAt: string, hours = 24): boolean {
  return Date.now() - new Date(updatedAt).getTime() < hours * 60 * 60 * 1000;
}

export default function MvpScopeBuilder() {
  const { ideas } = useIdeas();
  const focusedIdea = ideas.find(i => i.is_focus);
  const { getArtifact, upsertArtifact } = useIdeaArtifacts(focusedIdea?.id || null);
  const artifact = focusedIdea ? getArtifact('mvp_scope') : undefined;

  const [generating, setGenerating] = useState(false);
  const [localContent, setLocalContent] = useState<Record<string, unknown> | null>(null);
  const [localSources, setLocalSources] = useState<SourceRef[]>([]);
  const [localConfidence, setLocalConfidence] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [showConfirm, setShowConfirm] = useState(false);
  const [researchMode, setResearchMode] = useState(true);

  const storedContent = artifact?.content as Record<string, unknown> | undefined;
  const displayContent = localContent || storedContent;
  const displaySources = localSources.length > 0 ? localSources : (storedContent?.___sources as SourceRef[] | undefined) || [];
  const displayConfidence = localContent ? localConfidence : (storedContent?.___confidence as string || 'Medium') as 'High' | 'Medium' | 'Low';
  const hasExisting = !!displayContent && Object.keys(displayContent).filter(k => !k.startsWith('___')).length > 0;
  const isCached = artifact?.updated_at ? isRecent(artifact.updated_at) : false;

  const generate = async () => {
    if (!focusedIdea) { toast.error('Set a focused idea first.'); return; }
    if (hasExisting && isCached && !showConfirm) {
      toast.info('Generated recently (< 24h). Click Regenerate to refresh.');
      return;
    }
    setGenerating(true);
    try {
      const res = await supabase.functions.invoke('generate-instrument', {
        body: {
          instrument_type: 'mvp_scope',
          mode: researchMode ? 'research' : 'fast',
          idea: {
            title: focusedIdea.title || '',
            brain_dump: focusedIdea.raw_input || '',
            template_type: focusedIdea.template_type || 'blank',
            structure: focusedIdea.structure || {},
          },
        },
      });
      if (res.error) throw res.error;
      const content = res.data?.content;
      if (!content) throw new Error('No content returned');

      const sources: SourceRef[] = res.data?.sources || [];
      const confidence = res.data?.confidence || 'Medium';

      setLocalContent(content);
      setLocalSources(sources);
      setLocalConfidence(confidence);

      // Persist with metadata
      await upsertArtifact('mvp_scope', { ...content, ___sources: sources, ___confidence: confidence, ___mode: res.data?.mode });
      toast.success('MVP scope generated');
    } catch (err) {
      console.error('Generation failed:', err);
      toast.error('Generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Filter out internal keys for display
  const cleanContent = displayContent
    ? Object.fromEntries(Object.entries(displayContent).filter(([k]) => !k.startsWith('___')))
    : undefined;

  return (
    <InstrumentLayout
      title="MVP Scope Builder"
      description="Define what belongs in your two-week MVP and what doesn't."
      icon={Box}
    >
      <div className="space-y-6 w-full min-w-0">
        {/* Mode toggle */}
        <div className="flex items-center gap-3">
          <Switch
            id="research-mode"
            checked={researchMode}
            onCheckedChange={setResearchMode}
            disabled={generating}
          />
          <Label htmlFor="research-mode" className="text-sm cursor-pointer">
            {researchMode ? '🔍 Research-backed (with sources, slower)' : '⚡ Fast (no web research)'}
          </Label>
        </div>

        {generating ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {researchMode ? 'Searching the web & generating MVP scope…' : 'Generating MVP scope…'}
            </p>
          </div>
        ) : hasExisting && cleanContent ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <ConfidenceBadge level={displayConfidence} />
              {isCached && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">
                  Generated recently
                </span>
              )}
            </div>
            <div className="rounded-lg border border-border bg-card p-5 space-y-5 w-full min-w-0 overflow-auto max-h-[65vh]">
              <ContentRenderer content={cleanContent} artifactType="mvp_scope" />
            </div>
            <SourcesAccordion sources={displaySources} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <Sparkles className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No output yet. Generate to get started.</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {!hasExisting && !generating && (
            <Button onClick={generate} className="gap-2">
              <Sparkles className="h-4 w-4" /> Generate
            </Button>
          )}
          {hasExisting && !generating && (
            <>
              <Button variant="outline" onClick={() => setShowConfirm(true)} className="gap-2">
                <RefreshCw className="h-4 w-4" /> Regenerate
              </Button>
              <Button variant="ghost" size="sm" onClick={() => {
                navigator.clipboard.writeText(formatContentAsText(cleanContent!, 'mvp_scope'));
                toast.success('Text copied');
              }} className="gap-1.5 text-xs">
                <Copy className="h-3.5 w-3.5" /> Copy text
              </Button>
              <Button variant="ghost" size="sm" onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(cleanContent, null, 2));
                toast.success('JSON copied');
              }} className="gap-1.5 text-xs">
                <Copy className="h-3.5 w-3.5" /> Copy JSON
              </Button>
            </>
          )}
        </div>
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate MVP scope?</AlertDialogTitle>
            <AlertDialogDescription>This will replace the existing output.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowConfirm(false); generate(); }}>Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </InstrumentLayout>
  );
}
